import isNull from 'lodash/isNull'
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects'

import { updateLabelPositionAndText } from '../utils/updateLabelPositionAndText'
import { handleToggleDrawableGroups2D } from './handleToggleDrawableGroups'
import {
    handleToggleMaterialFlagsVisibility,
    toggleMaterialFlagsVisibilityAction,
} from './handleToggleMaterialFlagsVisibility'
import { drawAndLabelPaths, drawPageFlags, selectMarkupState, sharedMarkupOperations } from './markupDocument'
import { ActiveFloor } from '../../../models/activeFloor'
import { DocumentMapping } from '../../../models/documentMapping'
import { NormalizedFlag } from '../../../models/flags'
import { IMaterialModificationConflict } from '../../../models/masterSetPlan'
import { IToolObject } from '../../../models/tool'
import { PROJECT_PAGE_TAB_VALUES } from '../../../shared/constants/project-settings-names'
import { isHeaderAndBeamGroup } from '../../../shared/services/drawable-groups-service'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import { flash2D } from '../../lib/animations'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import {
    Color,
    Comment,
    Flag,
    Highlight,
    Image,
    Label,
    Overlay,
    PathTool,
    RegionTool,
    Workspace,
} from '../../lib/toolBoxes/2D/tools'
import addSelectFunctionalityToDrawable from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import addTooltipsToPath from '../../lib/utils/functionality-bindings/addTooltipsToPath'
import {
    addEstimatedOptionOverlayChunkId,
    DEFAULT_OVERLAY_COLOR,
    drawableLocationSelector,
    removeEstimatedOptionOverlayChunkId,
    selectCurrentEstimatedOptionOverlayChunkIds,
    selectOpacityDict,
    selectOverlayColorDict,
    setEstimatedOptionOverlayChunkId,
    setOverlayChunkId,
    updateOverlayColor,
    updateOverlayOpacity,
} from '../../slices/2D'
import {
    NormalizedDocumentChunk,
    selectDocumentMappings,
    selectNormalizedDocumentChunks,
    updateActiveFloorDocId,
} from '../../slices/documents'
import { GeometryGroup } from '../../slices/geometry'
import { sceneRendered } from '../../slices/loading'
import { selectSavedMaterialModifications, updateConflictResolutionFlag } from '../../slices/masterSetPlan'
import { MaterialFlagsState, selectMaterialFlags } from '../../slices/materialFlags'
import { getActiveTab } from '../../slices/navigation'
import { selectVisibility, ToolsState } from '../../slices/tools'
import { IMUP2DDrawableLocation } from '../../types'

export interface DocumentMarkupStoreState {
    drawableLocations: IMUP2DDrawableLocation[]
    openingGroups: GeometryGroup[]
    areaOpacity: ToolsState['areaOpacityValue']
    lineOpacity: ToolsState['lineOpacityValue']
    activeFloor: ActiveFloor | null
    flags: MaterialFlagsState['flags']
    toolObjects: IToolObject[]
}

export type OverlayMarkupTools = [
    Workspace,
    Overlay,
    RegionTool,
    Color,
    Comment,
    Highlight,
    Image,
    Flag,
    Label,
    PathTool
]

export function generateMaterialModsToAdjustAndHide(
    workspaceTool: Workspace,
    savedMaterialModifications: IMaterialModificationConflict[],
    drawableLocations: IMUP2DDrawableLocation[],
    overlayId: number
) {
    const drawableItems: paper.Item[] = workspaceTool.getAllDrawableItems()

    // sort material modifications into groups to be removed and to be adjusted
    return savedMaterialModifications.reduce(
        (acc, item) => {
            if (item.option_ids.includes(overlayId)) {
                const foundDrawable = drawableLocations.find(
                    (loc) => loc.opening_location_id === item.material_location_id
                )
                const paperItem = drawableItems.find((pI) => pI.data.opening_location_id === item.material_location_id)

                if (foundDrawable && paperItem) {
                    workspaceTool.compareCoordinatesUsingAPIModels(foundDrawable, item.coordinates, item.cutouts || [])
                        ? acc.matModsToHide.push(paperItem)
                        : acc.matModsToAdjust.push(paperItem)
                }
            }

            return acc
        },
        { matModsToHide: [] as paper.Item[], matModsToAdjust: [] as paper.Item[] }
    )
}

export function* modifyMaterialBasedOnConflicts(workspaceTool: Workspace, labelTool: Label, overlayId: number) {
    const savedMaterialModifications: IMaterialModificationConflict[] = yield select(selectSavedMaterialModifications)
    const drawableLocations: IMUP2DDrawableLocation[] = yield select(drawableLocationSelector)

    // sort material modifications into groups to be removed and to be adjusted
    const { matModsToHide, matModsToAdjust } = generateMaterialModsToAdjustAndHide(
        workspaceTool,
        savedMaterialModifications,
        drawableLocations,
        overlayId
    )

    // remove items that were deleted in conflict resolution
    yield all(matModsToHide.map((item) => item.remove()))

    const foundMatMods: IMaterialModificationConflict[] = []
    // adjust items that were adjusted in conflict resolution

    matModsToAdjust.map((item) => {
        const foundMatMod = savedMaterialModifications.find(
            (mm) => mm.material_location_id === item.data.opening_location_id && mm.option_ids.includes(overlayId)
        )

        const drawableLocation = drawableLocations.find(
            (dl) => dl.opening_location_id === item.data.opening_location_id
        )

        if (foundMatMod && drawableLocation) {
            foundMatMods.push(foundMatMod)
            const modCutouts = foundMatMod.cutouts || []
            const cutoutsToApply = modCutouts

            workspaceTool.updateItemCoordinates(item, foundMatMod.coordinates, cutoutsToApply, (item) => {
                addSelectFunctionalityToDrawable(item)
                addTooltipsToPath(item, workspaceTool)
                item.data.onFrameHandler = flash2D.bind(item)
                item.data.selectable = false
                item.data.isMultiSelectable = false
                workspaceTool.sendItemToBackBeforeRaster(item)
            })
            if (isHeaderAndBeamGroup(drawableLocation.drawing_type, drawableLocation.settings)) {
                updateLabelPositionAndText(
                    labelTool,
                    item,
                    drawableLocation.drawing_type,
                    drawableLocation.settings,
                    labelTool.convertPaperItemToAPICoordinateModel(item.children[0]).measurements.quantity ?? 0
                )
            }
        }
    })

    return foundMatMods
}

export function restoreVisibleMaterialSelection(items: paper.Item[]): void {
    items.forEach((item) => {
        if (item.isInserted() && item.visible && !isNonAreaJoistLine(item)) {
            item.data.selectable = true
        }
    })
}

export function* handleBaseDocument(
    overlayTool: Overlay,
    tools: {
        workspaceTool: Workspace
        imageTool: Image
        flagTool: Flag
        colorTool: Color
        pathTool: PathTool
        labelTool: Label
        regionTool: RegionTool
        highlightTool: Highlight
        commentTool: Comment
    },
    baseChunkId: number,
    normalizedChunks: NormalizedDocumentChunk[],
    markupState: DocumentMarkupStoreState
) {
    yield call(overlayTool.resetOverlayRasters)
    yield call(overlayTool.resetSelectedImagesAndCrossHairs)
    yield call(overlayTool.lockOverlay, false)
    yield put(updateConflictResolutionFlag(false))
    const baseHomeChunk = normalizedChunks.find((chunk) => chunk.id === baseChunkId)

    if (!baseHomeChunk) return null
    yield put(updateActiveFloorDocId(baseHomeChunk.id))
    const { flags, toolObjects, drawableLocations, openingGroups, lineOpacity, areaOpacity } = markupState

    yield sharedMarkupOperations(
        tools,
        baseHomeChunk,
        [],
        flags,
        toolObjects,
        drawableLocations,
        openingGroups,
        lineOpacity,
        areaOpacity
    )
    const { isMaterialFlagsVisible } = yield select(selectVisibility)

    yield call(handleToggleMaterialFlagsVisibility, {
        payload: isMaterialFlagsVisible,
        type: toggleMaterialFlagsVisibilityAction.type,
    })

    return baseHomeChunk
}

// Draws optId's overlay onto the canvas without disturbing other active overlays. Shared by the
// replace and add flows; activeOptionIds decides which already-drawn materials survive.
function* insertEstimatedOptionOverlay(
    tools: {
        workspaceTool: Workspace
        overlayTool: Overlay
        regionTool: RegionTool
        labelTool: Label
        flagTool: Flag
    },
    optId: number,
    activeOptionIds: number[],
    markupState: Pick<
        DocumentMarkupStoreState,
        'drawableLocations' | 'flags' | 'openingGroups' | 'areaOpacity' | 'lineOpacity'
    >,
    normalizedChunks: NormalizedDocumentChunk[]
) {
    const { workspaceTool, overlayTool, regionTool, labelTool, flagTool } = tools
    const { drawableLocations, flags, openingGroups, areaOpacity, lineOpacity } = markupState

    const chunkOpacityDict: Record<number, number> = yield select(selectOpacityDict)
    const possibleChunk: NormalizedDocumentChunk | undefined = normalizedChunks.find(
        (docChunk) => docChunk.id === optId
    )

    const overlayColorDict: Record<number, string> = yield select(selectOverlayColorDict)

    const documentMappings: DocumentMapping[] = yield select(selectDocumentMappings)
    const overlayMapping = documentMappings.find((map) => map.document_chunk_id === optId)
    const baseHomeMapping = documentMappings.find(
        (map) => map.id === overlayMapping?.additional_data.estimatedBaseHomeId
    )

    if (!possibleChunk) return

    const persistedOpacity = overlayMapping?.additional_data.overlayOpacity
    const persistedColor = overlayMapping?.additional_data.overlayColor
    const hasSessionOpacity = isFinite(chunkOpacityDict[optId])
    const hasPersistedOpacity = persistedOpacity !== undefined && isFinite(persistedOpacity)

    // Falls back to the last-saved value (lockOverlay.ts) until changed this session -- otherwise
    // a fresh reload would show opacity/color as reset.
    const opacityPercent = hasSessionOpacity ? chunkOpacityDict[optId] : persistedOpacity
    const overlayColor: string = overlayColorDict[optId] ?? persistedColor ?? DEFAULT_OVERLAY_COLOR
    const chunkOpacity: number =
        opacityPercent !== undefined && isFinite(opacityPercent) ? opacityPercent / 100 : Overlay.DEFAULT_OPACITY

    // Seed the dicts too so toolbar controls reflect the persisted value, not just the raster.
    if (!hasSessionOpacity && hasPersistedOpacity) {
        yield put(updateOverlayOpacity({ id: optId, opacity: persistedOpacity as number }))
    }

    if (overlayColorDict[optId] === undefined && persistedColor !== undefined) {
        yield put(updateOverlayColor({ id: optId, color: persistedColor }))
    }

    const alreadyDrawnItemsToHide: paper.Item[] = workspaceTool.getItemsWithCriteria(
        'data',
        (data) => data?.drawable_id || data?.aiSuggestion?.id
    )

    const baseHomeItems = new Set<paper.Item>()

    alreadyDrawnItemsToHide.forEach((item) => {
        const itemDocumentChunkId = item.data.document_chunk_id
        const isBaseHomeItem = itemDocumentChunkId === baseHomeMapping?.document_chunk_id
        const isAnotherActiveOptionItem = !isBaseHomeItem && activeOptionIds.includes(itemDocumentChunkId)

        if (isBaseHomeItem) {
            // Base home materials stay on canvas but must never be selectable/editable from an option's view.
            item.data.selectable = false
            item.data.isMultiSelectable = false
            baseHomeItems.add(item)
        } else if (!isAnotherActiveOptionItem && baseHomeMapping) {
            // Belongs to an option that's no longer active -- stale, remove it. Skipped when the
            // base home never resolved, since then nothing on the canvas can be told apart from
            // its materials and culling would clear the lot.
            item.remove()
        }
        // else: belongs to another active option -- leave its selectability untouched.
    })

    const { src, id } = possibleChunk

    yield call(overlayTool.insertOverlay, src, id, chunkOpacity, overlayColor)

    const overlayCenter = overlayMapping?.overlay_center

    yield put(updateConflictResolutionFlag(false))

    if (!overlayMapping || !overlayCenter || !overlayCenter.length) {
        yield call(overlayTool.resetOverlayRasters)
        yield call(overlayTool.resetSelectedImagesAndCrossHairs)
        throw new Error(`Either there is no document mapping associated with ${id}, or overlay center was not set`)
    }

    yield call(overlayTool.updateOverlayScale, id, overlayMapping.overlay_scale ?? 1)

    yield call(overlayTool.rotateImageRaster, id, overlayMapping.overlay_rotation)

    yield call(overlayTool.setOverlayPositionCoordinates, id, overlayCenter)

    // Master Plan Set projects never have regions, unlike drawAndLabelPaths' other callers.
    const regionPaths: paper.Path[] = []

    yield call(
        drawAndLabelPaths,
        optId,
        { lineOpacity, areaOpacity },
        drawableLocations,
        regionPaths,
        workspaceTool,
        regionTool
    )

    // determine ids of hidden drawables groups
    const hiddenDrawableGroupIds: Set<number> = openingGroups.reduce((groupIds, { id, isActive }) => {
        if (Object.values(isActive).includes(false)) {
            groupIds.add(id)
        }

        return groupIds
    }, new Set<number>())

    // build visibility predicate with hidden drawable group ids
    const visibilityTest = (item: paper.Item): boolean =>
        !hiddenDrawableGroupIds.has(item.data.opening_group_id) && !isNonAreaJoistLine(item)

    // hide drawable groups per visibility test predicate
    yield call(workspaceTool.toggleDrawablesWithCondition, visibilityTest)

    yield call(modifyMaterialBasedOnConflicts, workspaceTool, labelTool, optId)

    // Restore canvas selection for materials still visible after conflict resolution, but keep base
    // home items locked -- restoreVisibleMaterialSelection doesn't know about that distinction.
    restoreVisibleMaterialSelection(alreadyDrawnItemsToHide.filter((item) => !baseHomeItems.has(item)))

    // Hide all drawables that are inactive by default
    yield fork(handleToggleDrawableGroups2D)

    yield call(overlayTool.lockOverlay, true)

    // Never reset the flag cache here -- this runs once per active option, and resetting would
    // drop the flags already cached for the base page and any other active option.
    yield call(drawPageFlags, { flagTool, workspaceTool, flags, documentId: optId, shouldResetFlagCache: false })

    yield put(sceneRendered())
}

export function* handleInsertEstimatedOptionOverlay(action: ReturnType<typeof setEstimatedOptionOverlayChunkId>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (isNull(manager)) return

        // get markup state from redux store
        const {
            openingGroups,
            areaOpacity,
            lineOpacity,
            drawableLocations,
            flags,
            toolObjects,
        }: DocumentMarkupStoreState = yield select(selectMarkupState)

        // get tools from manager
        const [
            workspaceTool,
            overlayTool,
            regionTool,
            colorTool,
            commentTool,
            highlightTool,
            imageTool,
            flagTool,
            labelTool,
            pathTool,
        ]: OverlayMarkupTools = yield call(manager.getTools, [
            Workspace.NAME,
            Overlay.NAME,
            RegionTool.NAME,
            Color.NAME,
            Comment.NAME,
            Highlight.NAME,
            Image.NAME,
            Flag.NAME,
            Label.NAME,
            PathTool.NAME,
        ])

        const activeTab: PROJECT_PAGE_TAB_VALUES = yield select(getActiveTab)

        if (activeTab !== PROJECT_PAGE_TAB_VALUES.DIGITIZER) return

        const normalizedChunks: NormalizedDocumentChunk[] = yield select(selectNormalizedDocumentChunks)

        if (action.payload.base) {
            yield* handleBaseDocument(
                overlayTool,
                {
                    workspaceTool,
                    imageTool,
                    flagTool,
                    colorTool,
                    pathTool,
                    labelTool,
                    regionTool,
                    highlightTool,
                    commentTool,
                },
                action.payload.base,
                normalizedChunks,
                {
                    drawableLocations: drawableLocations.filter((loc) => loc.document_chunk_id === action.payload.base),
                    flags,
                    toolObjects,
                    openingGroups,
                    lineOpacity,
                    areaOpacity,
                    activeFloor: null,
                }
            )

            if (action.payload.opt === null) {
                yield put(sceneRendered())

                return
            }
        }

        yield put(setOverlayChunkId(null))

        if (action.payload.opt === null) return

        // additionalOpts carries the rest of a base home's locked options (the tree's "+N" row
        // only carries the first), so all of them get redrawn.
        const activeOptionIds = [action.payload.opt, ...(action.payload.additionalOpts ?? [])]

        yield* insertEstimatedOptionOverlay(
            { workspaceTool, overlayTool, regionTool, labelTool, flagTool },
            action.payload.opt,
            activeOptionIds,
            { drawableLocations, flags, openingGroups, areaOpacity, lineOpacity },
            normalizedChunks
        )

        for (const additionalOptId of action.payload.additionalOpts ?? []) {
            yield* insertEstimatedOptionOverlay(
                { workspaceTool, overlayTool, regionTool, labelTool, flagTool },
                additionalOptId,
                activeOptionIds,
                { drawableLocations, flags, openingGroups, areaOpacity, lineOpacity },
                normalizedChunks
            )
        }
    } catch (error) {
        yield call(console.error, error as any)
    }
}

// Adds an option on top of whatever's already active (Ctrl/Cmd+click) -- skips
// handleBaseDocument since that would wipe the other active overlays.
export function* handleAddingEstimatedOptionOverlay(action: ReturnType<typeof addEstimatedOptionOverlayChunkId>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (isNull(manager)) return

        const { openingGroups, areaOpacity, lineOpacity, drawableLocations, flags }: DocumentMarkupStoreState =
            yield select(selectMarkupState)

        const [workspaceTool, overlayTool, regionTool, , , , , flagTool, labelTool]: OverlayMarkupTools = yield call(
            manager.getTools,
            [
                Workspace.NAME,
                Overlay.NAME,
                RegionTool.NAME,
                Color.NAME,
                Comment.NAME,
                Highlight.NAME,
                Image.NAME,
                Flag.NAME,
                Label.NAME,
                PathTool.NAME,
            ]
        )

        const activeTab: PROJECT_PAGE_TAB_VALUES = yield select(getActiveTab)

        if (activeTab !== PROJECT_PAGE_TAB_VALUES.DIGITIZER) return

        const normalizedChunks: NormalizedDocumentChunk[] = yield select(selectNormalizedDocumentChunks)
        const activeOptionIds: number[] = yield select(selectCurrentEstimatedOptionOverlayChunkIds)

        yield put(setOverlayChunkId(null))

        yield* insertEstimatedOptionOverlay(
            { workspaceTool, overlayTool, regionTool, labelTool, flagTool },
            action.payload,
            activeOptionIds,
            { drawableLocations, flags, openingGroups, areaOpacity, lineOpacity },
            normalizedChunks
        )
    } catch (error) {
        yield call(console.error, error as any)
    }
}

// Removes one option's raster/materials/flags without disturbing anything else active. Region
// paths are always base-home-only (never per-option), so they're untouched here.
export function* handleRemovingEstimatedOptionOverlay(action: ReturnType<typeof removeEstimatedOptionOverlayChunkId>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return

    const [workspaceTool, overlayTool, flagTool]: [Workspace, Overlay, Flag] = yield call(manager.getTools, [
        Workspace.NAME,
        Overlay.NAME,
        Flag.NAME,
    ])

    yield call(overlayTool.removeOverlayById, action.payload)

    const materialItems: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => data.document_chunk_id === action.payload
    )

    materialItems.forEach((item) => item.remove())

    // Flag items aren't tagged with document_chunk_id, and the Flag tool's own bookkeeping only
    // tracks the most-recently-drawn option -- recompute matching opening_links from flags state
    // instead and remove those paper items directly.
    const flags: NormalizedFlag[] = yield select(selectMaterialFlags)
    const linksToRemove = flags.flatMap((flag) =>
        flag.opening_links
            .filter((link) => link.document_chunk_id === action.payload)
            .map((link) => ({ flagId: flag.id, linkId: link.id }))
    )

    for (const { flagId, linkId } of linksToRemove) {
        yield call(flagTool.removeFlagByFlagIdAndLinkId, flagId, linkId)
    }
}

export function* watchForSelectingNewEstimatedOptionOverlay() {
    yield takeLatest(setEstimatedOptionOverlayChunkId.type, handleInsertEstimatedOptionOverlay)
}

export function* watchForAddingEstimatedOptionOverlay() {
    yield takeLatest(addEstimatedOptionOverlayChunkId.type, handleAddingEstimatedOptionOverlay)
}

export function* watchForRemovingEstimatedOptionOverlay() {
    yield takeLatest(removeEstimatedOptionOverlayChunkId.type, handleRemovingEstimatedOptionOverlay)
}
